<template>
  <section
    class="app-promo-banner pb-size-11"
    :class="surfaceClass"
    data-test-id="app-promo-banner"
    :aria-label="title"
    :aria-busy="loading || undefined"
  >
    <!-- The dark slice the phones hang onto. Painted by its own box rather than
         by the section, so the section is free to carry the panel's colour. -->
    <div
      v-if="backgroundColor"
      aria-hidden="true"
      data-test-id="dark-band"
      class="app-promo-banner__band"
    />

    <!-- Coloured panel behind the headline; the phones hang past its bottom edge -->
    <div
      v-if="backgroundColor"
      aria-hidden="true"
      data-test-id="headline-panel"
      class="app-promo-banner__panel"
    />

    <!-- Headline + call to action -->
    <div
      class="app-promo-banner__headline flex flex-col items-center text-center px-size-5 py-size-11 gap-size-6"
      data-test-id="headline"
    >
      <component
        :is="titleHeadingTag"
        v-skeleton="loading"
        skeleton-height="60px"
        skeleton-radius="16px"
        data-test-id="title"
        class="heading-jumbo flex flex-col items-center"
      >
        <span class="text-text-headers">{{ title }}</span>
        <span
          v-if="titleHighlight.length"
          data-test-id="title-highlight"
          class="slot text-text-primary"
        >
          <transition :name="titleHighlight.length > 1 ? 'slot-roll' : ''">
            <span :key="currentHighlight" class="slot-item">{{
              currentHighlight
            }}</span>
          </transition>
        </span>
      </component>

      <div
        class="flex flex-col sm:flex-row items-center gap-size-4"
        v-skeleton="loading"
        skeleton-widht="91px"
        skeleton-height="48px"
        skeleton-radius="var(--size-3)"
      >
        <a
          :href="buttonPrimary.url || undefined"
          :target="buttonPrimary.target || undefined"
          :rel="relFor(buttonPrimary)"
        >
          <BaseButton
            data-test-id="primary-button"
            class="theme primary"
            :label="buttonPrimary.label"
            :aria-label="buttonPrimary.alt || undefined"
          />
        </a>
        <a
          v-if="buttonSecondary"
          :href="buttonSecondary?.url || undefined"
          :target="buttonSecondary?.target || undefined"
          :rel="buttonSecondary && relFor(buttonSecondary)"
        >
          <BaseButton
            data-test-id="secondary-button"
            class="theme secondary"
            :label="buttonSecondary?.label"
            :aria-label="buttonSecondary?.alt || undefined"
          />
        </a>
      </div>
    </div>

    <!-- Phone mockups, crossing the panel edge onto the dark background -->
    <div
      class="phone-stage mx-auto"
      :class="loading && !backgroundColor ? 'phone-stage--loading' : ''"
    >
      <!-- Without a panel, the dark band is what the phones sit over -->
      <div
        v-if="!backgroundColor"
        aria-hidden="true"
        data-test-id="dark-band"
        class="absolute inset-x-0 bottom-0 z-0 h-[42%] bg-white"
      />

      <!-- Blurred green glow centred behind both mockups -->
      <div
        v-if="!loading"
        aria-hidden="true"
        data-test-id="phone-glow"
        class="phone-glow"
      />

      <div
        class="phone-row relative flex justify-center px-size-5"
        v-skeleton="loading"
        skeleton-widht="100%"
        :skeleton-height="!backgroundColor ? '624px' : '229px'"
        skeleton-radius="var(--size-5)"
      >
        <!-- v-skeleton sits on <picture>, not on the <img>: a replaced element
             paints its own content over any placeholder. -->
        <picture v-if="leftPhone.image" class="phone phone-left">
          <source
            v-if="leftPhone.imageMobile"
            :srcset="leftPhone.imageMobile"
            media="(max-width: 767.98px)"
          />
          <img
            data-test-id="left-phone"
            class="block"
            :src="leftPhone.image"
            :alt="leftPhone.alt"
          />
        </picture>
        <picture v-if="rightPhone.image" class="phone phone-right">
          <source
            v-if="rightPhone.imageMobile"
            :srcset="rightPhone.imageMobile"
            media="(max-width: 767.98px)"
          />
          <img
            data-test-id="right-phone"
            class="block"
            :src="rightPhone.image"
            :alt="rightPhone.alt"
          />
        </picture>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
/**
 * AppShowcaseModule is a full-width promotional section that invites the user to
 * install the CA app. It shows a headline whose highlighted word cycles through
 * a list of strings with a vertical "slot" animation, a primary call-to-action
 * button, an optional secondary one, and two overlapping phone mockups that
 * float gently in counter-phase over a dark band, lit from behind by a blurred
 * green glow.
 */
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { BaseButton } from "ca-online-design-system";
import { normalizeHeadingTag } from "../../../utils/heading";

defineOptions({
  name: "AppShowcaseModule",
});

export interface PhoneMockup {
  /** Url of the mockup image. Optional. If not provided, the mockup is hidden.
   * The mockup is laid out from CSS, not from the file's own dimensions, so a
   * denser export sharpens it without changing the size it is painted at. */
  image?: string;
  /** Url of the mockup image in mobile. Optional. If not provided, the mockup is hidden. */
  imageMobile?: string;
  /** Alternative text for the mockup. Optional. */
  alt?: string;
}

export interface PromoButton {
  /** Visible label of the call-to-action button. Defaults to `"Label"`. */
  label?: string;
  /** Accessible label, for when the visible one is not descriptive enough. Optional. */
  alt?: string;
  /** Url the button links to. Optional. If not provided, the button does not navigate. */
  url?: string;
  /** Where to open the url, e.g. `"_blank"`. Optional. Defaults to the same tab. */
  target?: string;
}

interface Props {
  /** First (neutral) part of the headline */
  title?: string;
  /** Semantic element used for the module title. */
  titleHeadingType?: string;
  /** Highlighted words that rotate, in order, with a vertical slot animation */
  titleHighlight?: string[];
  /** Primary call-to-action button configuration */
  buttonPrimary?: PromoButton;
  /** Secondary call-to-action button configuration. Optional. If not provided, the button is hidden. */
  buttonSecondary?: PromoButton;
  /** Background of the headline block. Optional. When set, the headline sits on
   * this colour with a rounded bottom and the rest of the module turns dark. */
  backgroundColor?: "white" | "solarized";
  /** Left phone mockup, the one stacked behind */
  leftPhone?: PhoneMockup;
  /** Right phone mockup, the one stacked in front */
  rightPhone?: PhoneMockup;
  /** While true the module keeps its shape but shows skeleton placeholders
   * instead of the headline, the buttons and the mockups. */
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: "",
  titleHeadingType: "H2",
  titleHighlight: () => [],
  buttonPrimary: () => ({ label: "Label" }),
  buttonSecondary: undefined,
  backgroundColor: undefined,
  leftPhone: () => ({}),
  rightPhone: () => ({}),
  loading: false,
});

const titleHeadingTag = computed(() =>
  normalizeHeadingTag(props.titleHeadingType),
);

/** How long, in ms, each highlight stays visible before the next one rolls in. */
const HIGHLIGHT_INTERVAL_MS = 2000;

/** Section modifier per supported background colour. It carries the colour as a
 * custom property that both the section and the panel read, so the two can
 * never be painted in different colours. */
const SURFACE_CLASSES = {
  white: "app-promo-banner--white",
  solarized: "app-promo-banner--solarized",
} as const;

const surfaceClass = computed(() => [
  props.backgroundColor && "app-promo-banner--has-background",
  props.backgroundColor && SURFACE_CLASSES[props.backgroundColor],
  props.loading && "app-promo-banner--loading",
]);

const relFor = (button: PromoButton) =>
  button.target === "_blank" ? "noopener noreferrer" : undefined;

const index = ref(0);

const currentHighlight = computed(
  () => props.titleHighlight[index.value] ?? props.titleHighlight[0] ?? "",
);

let timer: ReturnType<typeof setInterval> | undefined;

const stopRotation = () => {
  if (timer) {
    clearInterval(timer);
    timer = undefined;
  }
};

const startRotation = () => {
  stopRotation();
  // Nothing to roll while the headline is a placeholder.
  if (props.loading) return;
  if (props.titleHighlight.length > 1) {
    timer = setInterval(() => {
      index.value = (index.value + 1) % props.titleHighlight.length;
    }, HIGHLIGHT_INTERVAL_MS);
  }
};

watch(
  () => [props.titleHighlight, props.loading],
  () => {
    index.value = 0;
    startRotation();
  },
);

onMounted(startRotation);
onUnmounted(stopRotation);
</script>

<style lang="postcss" scoped>
.app-promo-banner {
  @apply relative w-full;
}

/* How far up the module's bottom edge the coloured surface stops, and how
   tightly it rounds there. Figma: 394 / 32 on desktop, 243 / 24 on mobile. In
   px rather than a percentage of the module, because the module's height moves
   with the headline while this distance does not. */
.app-promo-banner {
  --panel-bottom: 394px;
  --panel-radius: 32px;
}

@media (max-width: 767.98px) {
  .app-promo-banner {
    --panel-bottom: 243px;
    --panel-radius: 24px;
  }
}

/* The placeholders are shorter than the real mockups, so the surface gives back
   part of that bottom slice while loading. */
.app-promo-banner--loading {
  --panel-bottom: 25%;
}

.app-promo-banner--white {
  --promo-surface: #fff;
}

/* DS solarized token — the package does not ship it as a CSS variable. */
.app-promo-banner--solarized {
  --promo-surface: #f6f7f2;
}

/* The section carries the *panel's* colour, not the dark one. That is what
   stops the hairlines: wherever the panel's edge lands a fraction of a pixel
   short of the box behind it, what shows through is the same colour, so there
   is nothing to see. The dark slice is painted by `__band` instead, and neither
   box has to reach outside the module to cover a seam. */
.app-promo-banner--has-background {
  background: var(--promo-surface);
}

/* Behind the panel, tall enough to sit under its rounded corners and no
   taller — it must not reach the module's top edge, or a seam there would show
   dark again. */
.app-promo-banner__band {
  position: absolute;
  bottom: -1px !important;
  inset: auto 0 0 0;
  height: calc(var(--panel-bottom) + var(--panel-radius));
  z-index: 0;
  background: var(--ca-color-neutrals-on-background);
}

/* The panel stops short of the module's bottom edge so the phones cross it. */
.app-promo-banner__panel {
  position: absolute;
  top: -1px !important;
  inset: 0 0 var(--panel-bottom) 0;
  z-index: 0;
  background: var(--promo-surface);
  border-bottom-left-radius: var(--panel-radius);
  border-bottom-right-radius: var(--panel-radius);
}

/* Headline and phones both sit above the panel. */
.app-promo-banner__headline,
.phone-stage {
  @apply relative z-[1];
}

.phone-stage {
  @apply isolate;
}

.phone-row {
  max-width: calc(327px + 2 * var(--size-5));
  margin-inline: auto;
}

/* The group only opens up to its full width from 768 up, in step with the
   mockups' own breakpoint: cap the row lower than the group and the group
   overflows the page instead of the gutter. */
@media (min-width: 768px) {
  .phone-row {
    max-width: calc(507px + 2 * var(--size-5));
  }
}

/* Loading without a panel: the placeholder is one tall block that hangs past
   nothing, so there is no overhang to reserve. `pb-size-11` is swapped out in
   the template rather than overridden here. */
.phone-stage--loading {
  padding: 0;
}

/* Sized per breakpoint, tracking the mockup group it sits behind: 507 wide from
   768px up, 327 below it. */
.phone-glow {
  @apply absolute left-1/2 top-1/2 z-[1] pointer-events-none;
  width: 507px;
  height: 476.296px;
  max-width: 100%;
  translate: -50% -50%;
  border-radius: 507px;
  opacity: 0.15;
  /* Figma spec. The two stops are the DS green-logo and BGContainerHigh tokens,
     neither of which the package ships as a CSS variable. */
  background: linear-gradient(143deg, #00965e 10.1%, #e5f4ef 104.13%);
  filter: blur(53.061222076416016px);
}

@media (max-width: 767.98px) {
  .phone-glow {
    width: 326.994px;
    height: 306.396px;
  }
}

/* Both mockups share one box, straight from Figma: 319 x 519 from 768px up and
   205.746 x 334.74 below it, always 59/96. */
.phone {
  width: 319px;
  height: 519px;
  aspect-ratio: 59 / 96;
  /* Their own height, rather than the flex line's. */
  align-self: flex-start;
  /* The two boxes plus the negative offset between them come to a little more
     than the row — 334.096 against 327 on mobile, 518 against 507 above 768 —
     so the pair reaches into the gutter. Without this the flex line would take
     that overflow back out of both widths and land them under spec; the row
     centres instead, so the overflow is split evenly and the pair stays on the
     module's centre line rather than drifting right. */
  flex-shrink: 0;
  /* Longhands, not the `animation` shorthand: a shorthand with no name in it is
     inert on its own, and the production minifier collapses it to
     `animation: none` — which also resets the duration to 0s, so the name
     .phone-left / .phone-right add below would animate over no time at all. */
  animation-duration: 3s;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
}

.phone-left {
  @apply z-10;
  animation-name: phone-float-inverse;
}

/* Pulled back so it starts at 199 and dropped by 97, per Figma. In px, like the
   sizes above: percentage margins resolve against the row's width, which no
   longer tracks the mockups now that these are a fixed size. */
.phone-right {
  @apply z-30;
  margin-left: -120px;
  margin-top: 97px;
  animation-name: phone-float;
}

/* Below 768 the same files are painted at 205.746 x 334.74, the mobile frame's
   size, and the offsets come down with them by the same 327 / 507. */
@media (max-width: 767.98px) {
  .phone {
    width: 205.746px;
    height: 334.74px;
  }

  .phone-right {
    margin-left: -77.396px;
    margin-top: 62.6px;
  }
}

/* Subtle, continuous float — vertical drift only, no rotation or scale. */
@keyframes phone-float {
  0%,
  100% {
    translate: 0px -6px;
  }

  50% {
    translate: 0px 6px;
  }
}

@keyframes phone-float-inverse {
  0%,
  100% {
    translate: 0px 6px;
  }

  50% {
    translate: 0px -6px;
  }
}

/* ── Loading state ─────────────────────────────────────────────────────────
   v-skeleton masks the real markup, so all that is left to say is how big each
   box should be while its content is still missing. */

/* Two jumbo lines, tracking the type ramp's line-height per breakpoint. */
.heading-jumbo[data-ca-skeleton] {
  @apply w-full max-w-[560px];
  height: 80px;
}

a[data-ca-skeleton] {
  width: 184px;
  height: 48px;
}

a[data-ca-skeleton] + a[data-ca-skeleton] {
  width: 140px;
}

/* Only the aspect ratios: the widths are the percentages above, so the module
   does not jump when the real images swap in. */
.phone[data-ca-skeleton] {
  @apply max-w-full;
  height: auto;
}

.phone-left[data-ca-skeleton],
.phone-right[data-ca-skeleton] {
  aspect-ratio: 59 / 96;
}

@media (min-width: 768px) {
  .heading-jumbo[data-ca-skeleton] {
    height: 96px;
  }
}

@media (min-width: 1024px) {
  .heading-jumbo[data-ca-skeleton] {
    height: 136px;
  }
}

.slot {
  @apply relative inline-grid overflow-hidden;
}

.slot-item {
  @apply whitespace-nowrap;
  grid-area: 1 / 1;
  justify-self: center;
}

.slot-roll-enter-active,
.slot-roll-leave-active {
  transition:
    transform 0.35s ease-in-out,
    opacity 0.35s ease-in-out;
}

.slot-roll-enter-from {
  @apply opacity-0;
  transform: translateY(100%);
}

.slot-roll-leave-to {
  @apply opacity-0;
  transform: translateY(-100%);
}

@media (prefers-reduced-motion: reduce) {
  .slot-roll-enter-active,
  .slot-roll-leave-active {
    transition: none;
  }

  .phone {
    animation: none;
  }
}
</style>
